// Derives display-ready pairing/liveness state from a Device document.
// Pure functions: no side effects, easy to unit-test or share between cards
// and detail pages.

export const PAIRING = {
    PAIRED: 'paired',
    AWAITING: 'awaiting',
    UNPAIRED: 'unpaired',
};

export const LIVENESS = {
    ONLINE: 'online',
    RECENT: 'recent',
    OFFLINE: 'offline',
    NEVER: 'never',
};

const ONLINE_MS = 60 * 1000;       // < 60s = online
const RECENT_MS = 10 * 60 * 1000;  // < 10min = recent

const COLORS = {
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    muted: '#6B7280',
    info: '#06B6D4',
};

export function getPairingState(device) {
    if (!device) return { kind: PAIRING.UNPAIRED, label: 'Not paired', color: COLORS.muted };

    if (device.claimedAt) {
        return {
            kind: PAIRING.PAIRED,
            label: 'Paired',
            color: COLORS.success,
            claimedAt: device.claimedAt,
        };
    }

    // The backend strips `claimCode` from list/get responses (it's a one-time
    // secret), but keeps `claimCodeExpiresAt`. An unexpired expiry timestamp
    // is a sufficient signal that a code is still outstanding.
    if (device.claimCodeExpiresAt) {
        const expiresAt = new Date(device.claimCodeExpiresAt).getTime();
        if (Number.isFinite(expiresAt) && expiresAt > Date.now()) {
            const minutes = Math.max(1, Math.round((expiresAt - Date.now()) / 60000));
            return {
                kind: PAIRING.AWAITING,
                label: `Awaiting pair · ${minutes}m`,
                color: COLORS.warning,
                expiresAt: device.claimCodeExpiresAt,
            };
        }
    }

    return { kind: PAIRING.UNPAIRED, label: 'Not paired', color: COLORS.muted };
}

export function getLivenessState(device) {
    if (!device || !device.lastSeenAt) {
        return { kind: LIVENESS.NEVER, label: 'Never seen', color: COLORS.muted };
    }
    const lastSeen = new Date(device.lastSeenAt).getTime();
    if (!Number.isFinite(lastSeen)) {
        return { kind: LIVENESS.NEVER, label: 'Never seen', color: COLORS.muted };
    }
    const ago = Date.now() - lastSeen;
    if (ago < ONLINE_MS) {
        return { kind: LIVENESS.ONLINE, label: 'Online', color: COLORS.success, lastSeenAt: device.lastSeenAt };
    }
    if (ago < RECENT_MS) {
        return { kind: LIVENESS.RECENT, label: 'Recently active', color: COLORS.info, lastSeenAt: device.lastSeenAt };
    }
    return { kind: LIVENESS.OFFLINE, label: 'Offline', color: COLORS.muted, lastSeenAt: device.lastSeenAt };
}

// "30 seconds ago", "5 minutes ago", "2 hours ago", "yesterday", "Mar 12"
export function relativeTime(iso) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '';
    const diffSec = Math.round((Date.now() - t) / 1000);
    if (diffSec < 5) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay === 1) return 'yesterday';
    if (diffDay < 7) return `${diffDay} days ago`;
    return new Date(iso).toLocaleDateString();
}
